import React from 'react'
import PropTypes from 'prop-types'
import { Col, Collapse, Descriptions, Row, Typography } from 'antd'
import { formatSecondsToTime } from '../../../../core/utils/services/date-utils'
import {
  formatCountAndRatio,
  formatMetricCount,
  formatMetricPercent,
  getMetricRatio,
} from '../../../utils'
import { renderValidationMetricItem } from './render-validation-metric'
import ValidationEntityMetrics from './validation-entity-metrics'
import ValidationFieldMetricsTable from './validation-field-metrics-table'

const { Panel } = Collapse

const ScrapingSessionValidation = ({ validationResult }) => {
  const metrics = validationResult?.metrics || {}
  const totals = metrics.totals || {}
  const coverage = validationResult?.coverage || {}
  const performance = validationResult?.performance || {}
  const missingRatio = getMetricRatio(
    totals.missingItemsCount,
    totals.idealItemsCount,
  )
  const newRatio = getMetricRatio(
    totals.newItemsCount,
    totals.currentItemsCount,
  )

  return (
    <Collapse>
      <Panel header="Validation" key="validation">
        {validationResult ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={8}>
                <Descriptions bordered size="small" column={1}>
                  {renderValidationMetricItem(
                    'Overall validation score',
                    formatMetricPercent(metrics.validationScore),
                  )}
                  {renderValidationMetricItem(
                    'F1 score',
                    formatMetricPercent(metrics.f1),
                  )}
                  {renderValidationMetricItem(
                    'Session average total similarity',
                    formatMetricPercent(metrics.totalSimilarity),
                  )}
                  {renderValidationMetricItem(
                    'Session average identity similarity',
                    formatMetricPercent(metrics.identitySimilarity),
                  )}
                  {renderValidationMetricItem(
                    'Session average content similarity',
                    formatMetricPercent(metrics.contentSimilarity),
                  )}
                </Descriptions>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Descriptions bordered size="small" column={1}>
                  {renderValidationMetricItem(
                    'Matched items (ratio)',
                    formatCountAndRatio(
                      totals.matchedItemsCount,
                      metrics.recall,
                    ),
                  )}
                  {renderValidationMetricItem(
                    'Exact items (rate)',
                    formatCountAndRatio(
                      totals.exactMatchesCount,
                      metrics.exactMatchRate,
                    ),
                  )}
                  {renderValidationMetricItem(
                    'Similar items (rate)',
                    formatCountAndRatio(
                      totals.similarMatchesCount,
                      metrics.similarMatchRate,
                    ),
                  )}
                  {renderValidationMetricItem(
                    'Missing items (ratio)',
                    formatCountAndRatio(totals.missingItemsCount, missingRatio),
                  )}
                  {renderValidationMetricItem(
                    'New items (ratio)',
                    formatCountAndRatio(totals.newItemsCount, newRatio),
                  )}
                </Descriptions>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Descriptions bordered size="small" column={1}>
                  {renderValidationMetricItem(
                    'Total ideal items',
                    formatMetricCount(totals.idealItemsCount),
                  )}
                  {renderValidationMetricItem(
                    'Total current items',
                    formatMetricCount(totals.currentItemsCount),
                  )}
                </Descriptions>
              </Col>
            </Row>
            <Collapse style={{ marginTop: 16 }}>
              <Panel header="Menu title metrics" key="menu-title-metrics">
                <ValidationEntityMetrics metrics={metrics.menuTitles} />
              </Panel>
              <Panel header="Dish metrics" key="dish-metrics">
                <ValidationEntityMetrics
                  metrics={metrics.dishes}
                  showPathWordCountRatio
                />
              </Panel>
              <Panel header="Response Coverage" key="coverage">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Expected menus',
                        formatMetricCount(coverage.expectedMenusCount),
                      )}
                      {renderValidationMetricItem(
                        'Responded menus',
                        formatMetricCount(coverage.respondedMenusCount),
                      )}
                      {renderValidationMetricItem(
                        'Missing responses',
                        formatMetricCount(coverage.missingResponsesCount),
                      )}
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Valid menus',
                        formatMetricCount(coverage.validMenusCount),
                      )}
                      {renderValidationMetricItem(
                        'Invalid menus',
                        formatMetricCount(coverage.invalidMenusCount),
                      )}
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Response coverage',
                        formatMetricPercent(coverage.responseCoverage),
                      )}
                      {renderValidationMetricItem(
                        'Valid-response ratio',
                        formatMetricPercent(coverage.validResponseRatio),
                      )}
                    </Descriptions>
                  </Col>
                </Row>
              </Panel>
              <Panel header="Scraping Performance" key="performance">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Total scraping duration',
                        performance.totalScrapingDuration == null
                          ? 'N/A'
                          : formatSecondsToTime(
                              performance.totalScrapingDuration,
                            ),
                      )}
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Average scraping duration',
                        performance.averageScrapingDuration == null
                          ? 'N/A'
                          : formatSecondsToTime(
                              performance.averageScrapingDuration,
                            ),
                      )}
                      {renderValidationMetricItem(
                        'Median scraping duration',
                        performance.medianScrapingDuration == null
                          ? 'N/A'
                          : formatSecondsToTime(
                              performance.medianScrapingDuration,
                            ),
                      )}
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <Descriptions bordered size="small" column={1}>
                      {renderValidationMetricItem(
                        'Dishes per second',
                        typeof performance.dishesPerSecond === 'number'
                          ? performance.dishesPerSecond.toFixed(2)
                          : 'N/A',
                      )}
                    </Descriptions>
                  </Col>
                </Row>
              </Panel>
              <Panel header="Per-field metrics" key="field-metrics">
                <ValidationFieldMetricsTable metrics={metrics} />
              </Panel>
            </Collapse>
          </>
        ) : (
          <Typography.Text type="secondary">
            Validation metrics are not available yet.
          </Typography.Text>
        )}
      </Panel>
    </Collapse>
  )
}

ScrapingSessionValidation.propTypes = {
  validationResult: PropTypes.object,
}

ScrapingSessionValidation.defaultProps = {
  validationResult: undefined,
}

export default React.memo(ScrapingSessionValidation)
